import { useEffect, useState } from "react";
import { FileText, Sparkles } from "lucide-react";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ChoixListe } from "@/pages/parametres/ChoixListe";

const VERSIONS = [{ code: "equipe", label: "Script de l'équipe" }, { code: "ia", label: "Version améliorée par l'IA" }, { code: "objections", label: "Objections" }];
const METIERS = [{ code: "agent", label: "Agent" }, { code: "confirmateur", label: "Confirmateur" }];

const Sections = ({ sections, ia, remplir }) => (
  <div className="space-y-3">
    {sections.map((s, i) => (
      <div key={i} className="at-soft p-3 space-y-1" data-testid={`script-section-${i}`}>
        <p className="at-label">{s.titre}</p>
        <p className="text-sm break-words">{remplir(s.texte)}</p>
        {ia && s.ajout_ia && <p className="text-sm font-semibold break-words flex gap-1.5" style={{ color: "var(--success)" }}><Sparkles size={14} className="shrink-0 mt-0.5" />{s.ajout_ia}</p>}
      </div>
    ))}
  </div>
);

const Contenu = ({ operation, metierInitial, contact }) => {
  const { user } = useAuth();
  const remplir = (t) => t.replace("{civilité nom}", contact || "Madame, Monsieur").replace("{prénom}", user.prenom || "");
  const direction = ["dirigeant", "responsable"].includes(user.role);
  const [ops, setOps] = useState([]);
  const [op, setOp] = useState(operation);
  const [metier, setMetier] = useState(metierInitial || (user.role === "confirmateur" ? "confirmateur" : "agent"));
  const [version, setVersion] = useState("equipe");
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/scripts").then((r) => { const l = [...new Set(r.data.map((s) => s.operation))]; setOps(l); setOp((o) => (l.includes(o) ? o : l[0])); }); }, []);
  useEffect(() => { if (op) api.get("/scripts/lire", { params: { operation: op, metier } }).then((r) => setD(r.data)).catch(() => setD(null)); }, [op, metier]);
  return (
    <div className="space-y-3 overflow-y-auto pb-4 min-w-0" data-testid="script-content">
      <div className={`grid gap-2 ${direction ? "grid-cols-2" : "grid-cols-1"}`}>
        <ChoixListe valeur={op} onChange={setOp} options={ops.map((o) => ({ code: o, label: o }))} testId="script-operation" />
        {direction && <ChoixListe valeur={metier} onChange={setMetier} options={METIERS} testId="script-metier" />}
      </div>
      <ChoixListe valeur={version} onChange={setVersion} options={VERSIONS} testId="script-version-select" />
      {d && version === "equipe" && <><Sections sections={d.equipe.sections} remplir={remplir} /><p className="text-xs at-muted">Modifié par {d.equipe.updated_by}{d.modifiable ? " · modifiable dans Paramètres > Scripts" : ""}</p></>}
      {d && version === "ia" && (
        <>
          <p className="text-xs at-muted" data-testid="script-ia-source">Basée sur {d.ia.appels_reussis} appel(s) réussi(s) (simulation). Rien n'est modifié tant que la direction ne l'applique pas.</p>
          <Sections sections={d.ia.sections} ia remplir={remplir} />
        </>
      )}
      {d && version === "objections" && (
        <ul className="space-y-2" data-testid="script-objections">
          {d.ia.objections.map((o) => <li key={o.objection} className="at-soft p-3 text-sm"><p className="font-bold">« {o.objection} »</p><p className="break-words">{o.reponse}</p></li>)}
        </ul>
      )}
    </div>
  );
};

export const BoutonScript = ({ operation, metier, contact, testId = "script-button", className = "at-btn at-btn-ghost" }) => {
  const [ouvert, setOuvert] = useState(false);
  return (
    <>
      <button type="button" onClick={() => setOuvert(true)} className={className} data-testid={testId}><FileText size={16} />Script</button>
      <Sheet open={ouvert} onOpenChange={setOuvert}>
        <SheetContent side="bottom" className="at-popover !rounded-b-none max-h-[88vh] flex flex-col p-4 md:p-6 md:max-w-2xl md:mx-auto" data-testid="script-panel">
          <SheetHeader className="text-left"><SheetTitle>Script d'appel</SheetTitle><SheetDescription className="text-xs at-muted">Choisissez la version à afficher.</SheetDescription></SheetHeader>
          {ouvert && <Contenu operation={operation} metierInitial={metier} contact={contact} />}
        </SheetContent>
      </Sheet>
    </>
  );
};
